// @flow
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';
import tenantsFileDropStyles from './Styles/TenantsFileDrop.Style';

// Import step components
import FileSelectView from './ImportSteps/FileSelectView';
import UploadingView from './ImportSteps/UploadingView';
import PreviewView from './ImportSteps/PreviewView';
import ProcessingView from './ImportSteps/ProcessingView';
import ResultsView from './ImportSteps/ResultsView';

// Redux actions
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { setTenantEnableUpload } from '../../reduxStore/modules/tenants/actions';

type ImportStep = 'select' | 'uploading' | 'preview' | 'processing' | 'results' | 'error';

type ParseResult = {
  totalRows: number,
  headers: Array<string>,
  sampleRows: Array<Object>,
  suggestedMapping: Object
};

type ImportStatus = {
  status: 'processing' | 'completed' | 'failed',
  totalRows: number,
  processedRows: number,
  created: number,
  skipped: number,
  failed: number,
  errors: Array<{row: number, phone: string, reason: string}>,
  completedAt?: string
};

type Props = {
  onImportComplete?: () => void
};

const TenantsDropzone = ({ onImportComplete }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = createClasses(tenantsFileDropStyles, theme);

  // Step state
  const [step, setStep] = useState<ImportStep>('select');

  // Import data state
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [columnMapping, setColumnMapping] = useState<{[string]: string} | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);

  // Reset all state
  const resetState = useCallback(() => {
    setStep('select');
    setFile(null);
    setJobId(null);
    setParseResult(null);
    setColumnMapping(null);
    setImportStatus(null);
  }, []);

  // Handle cancel - hide the dropzone
  const handleCancel = useCallback(() => {
    resetState();
    dispatch(setTenantEnableUpload(false));
  }, [dispatch, resetState]);

  // Handle file selection
  const handleFileSelected = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setStep('uploading');
  }, []);

  // Handle parse complete (after upload)
  const handleParseComplete = useCallback((result: ParseResult, newJobId: string) => {
    setParseResult(result);
    setJobId(newJobId);
    setStep('preview');
  }, []);

  // Handle mapping confirmed
  const handleMappingConfirmed = useCallback((mapping: {[string]: string}) => {
    setColumnMapping(mapping);
    setStep('processing');
  }, []);

  // Handle processing complete
  const handleProcessingComplete = useCallback((status: ImportStatus) => {
    setImportStatus(status);
    setStep('results');
  }, []);

  // Handle error
  const handleError = useCallback((errorMessage: string) => {
    dispatch(setSnackMessage({
      message: errorMessage,
      level: 'error',
      duration: 5000
    }));
    // Go back to select step on error
    setStep('select');
    setFile(null);
    setJobId(null);
    setParseResult(null);
    setColumnMapping(null);
  }, [dispatch]);

  // Handle done (after results)
  const handleDone = useCallback(() => {
    resetState();
    dispatch(setTenantEnableUpload(false));

    // Trigger customer list refresh
    if (onImportComplete) {
      onImportComplete();
    }

    // Show success message
    if (importStatus && importStatus.created > 0) {
      dispatch(setSnackMessage({
        message: `Successfully imported ${importStatus.created} customer${importStatus.created !== 1 ? 's' : ''}!`,
        level: 'success',
        duration: 5000
      }));
    }
  }, [dispatch, resetState, onImportComplete, importStatus]);

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 'select':
        return (
          <FileSelectView
            onFileSelected={handleFileSelected}
            onCancel={handleCancel}
          />
        );

      case 'uploading':
        if (!file) return null;
        return (
          <UploadingView
            file={file}
            onParseComplete={handleParseComplete}
            onError={handleError}
            onCancel={handleCancel}
          />
        );

      case 'preview':
        if (!parseResult) return null;
        return (
          <PreviewView
            parseResult={parseResult}
            onMappingConfirmed={handleMappingConfirmed}
            onCancel={handleCancel}
          />
        );

      case 'processing':
        if (!jobId || !columnMapping) return null;
        return (
          <ProcessingView
            jobId={jobId}
            columnMapping={columnMapping}
            onProcessingComplete={handleProcessingComplete}
            onError={handleError}
          />
        );

      case 'results':
        if (!importStatus) return null;
        return (
          <ResultsView
            importStatus={importStatus}
            onDone={handleDone}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={classes.container}>
      {renderStep()}
    </Box>
  );
};

export default TenantsDropzone;
